import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import _ from "lodash";
import moment from "moment";
import { getSummaries } from "../../services";
import { setMessage } from "../../store/slices/memberSlice";
import Pagination from "../../components/Pagination";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, MessageSquare, Clock, ArrowRight } from "lucide-react";


export default function SummaryLists() {
  const dispatch = useDispatch();
  const messages = useSelector((state) => state.members.message);
  const [offset, setOffset] = useState(0);
  const itemsPerPage = 10;

  const userProfile = useSelector((state) => state.user);


  const fetchMessage = async (offset) => {
    try {
      const formattedStartDate = moment().startOf('month').format("YYYY-MM-DD");
      const formattedEndDate = moment().format("YYYY-MM-DD");
      const response = await getSummaries(
        userProfile.userinfo.Id,
        formattedStartDate,
        formattedEndDate,
        offset,
        itemsPerPage
      );
      dispatch(setMessage(response?.data?.Result));
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  useEffect(() => {
    fetchMessage(offset);
  }, [offset]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-500" />
          Recent Summaries
        </h3>
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
          Total: {messages?.TotalCount || 0}
        </span>
      </div>

      <div className="grid gap-4">
        {(!_.isEmpty(messages) && messages?.TotalCount > 0) ? (
          <AnimatePresence mode="popLayout">
            {messages?.SummaryList?.map((msg, index) => {
              const formattedDate = format(
                new Date(msg.SummaryDate),
                "PPPP"
              );
              return (
                <motion.div
                  key={msg.Id || index}
                  initial="initial"
                  animate="animate"
                  whileHover="hover"
                  exit="exit"
                  variants={{
                    initial: { opacity: 0, x: -20 },
                    animate: { opacity: 1, x: 0, transition: { delay: index * 0.05 } },
                    exit: { opacity: 0, scale: 0.95 }
                  }}
                  className="group relative bg-white dark:bg-gray-900/40 border border-gray-100 dark:border-blue-gray-900 p-5 rounded-2xl hover:shadow-lg hover:shadow-blue-500/5 transition-all duration-300"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="px-3 py-1 bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-400 rounded-lg text-[11px] font-bold tracking-tight border border-gray-100 dark:border-white/5">
                        {format(new Date(msg.SummaryDate), "dd MMM yyyy")}
                      </div>
                    </div>

                    <div className="flex-grow">
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm font-medium">
                        {msg.Summary}
                      </p>
                      <motion.div
                        variants={{
                          initial: { height: 0, opacity: 0, marginTop: 0 },
                          hover: { height: "auto", opacity: 1, marginTop: 12 }
                        }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="flex items-center gap-1.5 text-[10px] text-gray-400 dark:text-gray-500 font-medium overflow-hidden"
                      >
                        <div className="w-1 h-1 rounded-full bg-blue-500/50" />
                        Submitted on {formattedDate}
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-20 bg-gray-50 dark:bg-gray-900/20 rounded-3xl border-2 border-dashed border-gray-200 dark:border-blue-gray-900"
          >
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
              <MessageSquare className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 font-medium">No request summary found yet.</p>
          </motion.div>
        )}
      </div>

      {(!_.isEmpty(messages) && messages?.TotalCount > 0) && (
        <div className="mt-8 flex justify-center">
          <Pagination
            totalCount={messages?.TotalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={(newOffset) => setOffset(newOffset)}
          />
        </div>
      )}
    </div>
  );
}
